import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    Award,
    BadgeCheck,
    BarChart3,
    Check,
    CircleCheck,
    DollarSign,
    Globe2,
    LayoutTemplate,
    Megaphone,
    MonitorSmartphone,
    Rocket,
    Search,
    ShieldCheck,
    Sparkles,
    Target,
    TrendingUp,
    Users,
    Zap,
} from 'lucide-react';
import {
    whatWeDoIconTokens,
    whatWeDoTokens,
    type WhatWeDoColorToken,
    type WhatWeDoIconToken,
} from './WhatWeDo.tokens';

export type WhatWeDoTheme = 'light' | 'dark' | 'system';

export type WhatWeDoLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type WhatWeDoImage = {
    src?: string;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
};

export type WhatWeDoIcon = WhatWeDoIconToken | React.ReactNode | React.ElementType;

export type WhatWeDoPoint = {
    id?: string | number;
    text?: React.ReactNode;
    title?: React.ReactNode;
    label?: React.ReactNode;
    icon?: WhatWeDoIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentColour?: string;
};

export type WhatWeDoBadge = {
    id?: string | number;
    title?: React.ReactNode;
    label?: React.ReactNode;
    value?: React.ReactNode;
    text?: React.ReactNode;
    icon?: WhatWeDoIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type WhatWeDoCta = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: WhatWeDoIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type WhatWeDoColors = Partial<Record<WhatWeDoColorToken, string>>;

export type WhatWeDoProps = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    points?: Array<React.ReactNode | WhatWeDoPoint>;
    pointIcon?: WhatWeDoIcon;
    badges?: WhatWeDoBadge[];
    image?: WhatWeDoImage | string;
    cta?: WhatWeDoCta | false;
    LinkComponent?: WhatWeDoLinkComponent;
    Link?: WhatWeDoLinkComponent;
    theme?: WhatWeDoTheme;
    darkMode?: boolean;
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
    accentDarkColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    colors?: WhatWeDoColors;
    colours?: WhatWeDoColors;
    maxWidth?: number | string;
    contentMaxWidth?: number | string;
    reverse?: boolean;
    showPattern?: boolean;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type WhatWeDoCssVars = CSSProperties &
    Record<`--what-we-do-${string}`, string | number | undefined>;

type NormalizedPoint = {
    id: string | number;
    text: React.ReactNode;
    icon?: WhatWeDoIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
};

type NormalizedBadge = {
    id: string | number;
    title: React.ReactNode;
    value?: React.ReactNode;
    text?: React.ReactNode;
    icon?: WhatWeDoIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentSoftColor?: string;
};

const t = whatWeDoTokens;

const iconRegistry: Record<WhatWeDoIconToken, React.ElementType> = {
    arrowRight: ArrowRight,
    award: Award,
    badgeCheck: BadgeCheck,
    barChart: BarChart3,
    check: Check,
    circleCheck: CircleCheck,
    dollar: DollarSign,
    globe: Globe2,
    layout: LayoutTemplate,
    megaphone: Megaphone,
    monitor: MonitorSmartphone,
    rocket: Rocket,
    search: Search,
    shieldCheck: ShieldCheck,
    sparkles: Sparkles,
    target: Target,
    trendingUp: TrendingUp,
    users: Users,
    zap: Zap,
};

const whatWeDoCss = `
.what-we-do {
  --what-we-do-background: ${t.colors.light.background};
  --what-we-do-surface: ${t.colors.light.surface};
  --what-we-do-text: ${t.colors.light.text};
  --what-we-do-text-soft: ${t.colors.light.textSoft};
  --what-we-do-text-muted: ${t.colors.light.textMuted};
  --what-we-do-border: ${t.colors.light.border};
  --what-we-do-badge-surface: ${t.colors.light.badgeSurface};
  --what-we-do-image-fallback: ${t.colors.light.imageFallback};
  --what-we-do-dot: ${t.colors.light.dot};
  --what-we-do-shadow: ${t.colors.light.shadow};
  --what-we-do-badge-shadow: ${t.colors.light.badgeShadow};
  --what-we-do-accent: ${t.colors.accent};
  --what-we-do-accent-dark: ${t.colors.accentDark};
  --what-we-do-accent-soft: ${t.colors.accentSoft};
  --what-we-do-accent-contrast: ${t.colors.accentContrast};
  --what-we-do-max-width: ${t.sizes.maxWidth}px;
  --what-we-do-content-max-width: ${t.sizes.contentMaxWidth}px;
  width: 100%;
  overflow: hidden;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
  background: var(--what-we-do-background);
  color: var(--what-we-do-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.what-we-do[data-theme="dark"] {
  --what-we-do-background: ${t.colors.dark.background};
  --what-we-do-surface: ${t.colors.dark.surface};
  --what-we-do-text: ${t.colors.dark.text};
  --what-we-do-text-soft: ${t.colors.dark.textSoft};
  --what-we-do-text-muted: ${t.colors.dark.textMuted};
  --what-we-do-border: ${t.colors.dark.border};
  --what-we-do-badge-surface: ${t.colors.dark.badgeSurface};
  --what-we-do-image-fallback: ${t.colors.dark.imageFallback};
  --what-we-do-dot: ${t.colors.dark.dot};
  --what-we-do-shadow: ${t.colors.dark.shadow};
  --what-we-do-badge-shadow: ${t.colors.dark.badgeShadow};
}
@media (prefers-color-scheme: dark) {
  .what-we-do[data-theme="system"] {
    --what-we-do-background: ${t.colors.dark.background};
    --what-we-do-surface: ${t.colors.dark.surface};
    --what-we-do-text: ${t.colors.dark.text};
    --what-we-do-text-soft: ${t.colors.dark.textSoft};
    --what-we-do-text-muted: ${t.colors.dark.textMuted};
    --what-we-do-border: ${t.colors.dark.border};
    --what-we-do-badge-surface: ${t.colors.dark.badgeSurface};
    --what-we-do-image-fallback: ${t.colors.dark.imageFallback};
    --what-we-do-dot: ${t.colors.dark.dot};
    --what-we-do-shadow: ${t.colors.dark.shadow};
    --what-we-do-badge-shadow: ${t.colors.dark.badgeShadow};
  }
}
.what-we-do,
.what-we-do * {
  box-sizing: border-box;
}
.what-we-do-shell {
  position: relative;
  display: grid;
  grid-template-columns: minmax(280px, 0.78fr) minmax(420px, 1.22fr);
  align-items: center;
  gap: ${t.spacing.desktopGap}px;
  width: 100%;
  max-width: var(--what-we-do-max-width);
  margin: 0 auto;
}
.what-we-do--reverse .what-we-do-content {
  order: 2;
}
.what-we-do--reverse .what-we-do-media-column {
  order: 1;
}
.what-we-do-content {
  position: relative;
  z-index: 2;
  max-width: var(--what-we-do-content-max-width);
  min-width: 0;
}
.what-we-do-eyebrow {
  display: inline-flex;
  align-items: center;
  margin: 0 0 22px;
  color: var(--what-we-do-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
}
.what-we-do-title {
  max-width: 620px;
  margin: 0;
  color: var(--what-we-do-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
}
.what-we-do-description {
  max-width: 560px;
  margin: 18px 0 0;
  color: var(--what-we-do-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
}
.what-we-do-points {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${t.spacing.pointsGap}px;
  width: 100%;
  margin: 30px 0 0;
  padding: 0;
  list-style: none;
}
.what-we-do-point {
  display: grid;
  grid-template-columns: ${t.sizes.pointIcon}px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  min-width: 0;
  color: var(--what-we-do-text);
  font-size: ${t.typography.point.fontSize}px;
  line-height: ${t.typography.point.lineHeight};
  font-weight: ${t.typography.point.fontWeight};
  letter-spacing: ${t.typography.point.letterSpacing};
}
.what-we-do-point-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${t.sizes.pointIcon}px;
  height: ${t.sizes.pointIcon}px;
  border: 2px solid var(--what-we-do-accent);
  border-radius: ${t.radius.icon}px;
  color: var(--what-we-do-accent);
  background: color-mix(in srgb, var(--what-we-do-accent) 7%, transparent);
}
.what-we-do-point-icon svg {
  width: 16px;
  height: 16px;
}
.what-we-do-icon-image {
  display: block;
  width: 18px;
  height: 18px;
  object-fit: contain;
}
.what-we-do-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: ${t.sizes.ctaMinHeight}px;
  margin-top: 32px;
  padding: 0 24px;
  border: 1px solid color-mix(in srgb, var(--what-we-do-accent-dark) 32%, transparent);
  border-radius: ${t.radius.button}px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--what-we-do-accent) 92%, #ffffff), var(--what-we-do-accent-dark));
  color: var(--what-we-do-accent-contrast);
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-decoration: none;
  box-shadow: 0 12px 24px color-mix(in srgb, var(--what-we-do-accent) 24%, transparent);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}
.what-we-do-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px color-mix(in srgb, var(--what-we-do-accent) 30%, transparent);
}
.what-we-do-cta:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--what-we-do-accent) 28%, transparent);
  outline-offset: 4px;
}
.what-we-do-cta svg,
.what-we-do-cta .what-we-do-icon-image {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
.what-we-do-media-column {
  position: relative;
  min-width: 0;
}
.what-we-do-pattern {
  position: absolute;
  right: -38px;
  bottom: -34px;
  z-index: 0;
  width: 168px;
  height: 210px;
  background-image: radial-gradient(circle, var(--what-we-do-dot) 2px, transparent 2px);
  background-size: 22px 22px;
  pointer-events: none;
}
.what-we-do-media {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: ${t.sizes.mediaMinHeight}px;
  overflow: hidden;
  border: 1px solid var(--what-we-do-border);
  border-radius: ${t.radius.image}px;
  background: var(--what-we-do-image-fallback);
  box-shadow: var(--what-we-do-shadow);
}
.what-we-do-image,
.what-we-do-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
  min-height: ${t.sizes.mediaMinHeight}px;
}
.what-we-do-image {
  object-fit: cover;
  transition: transform 520ms ease;
}
.what-we-do-media:hover .what-we-do-image {
  transform: scale(1.025);
}
.what-we-do-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  color: var(--what-we-do-text-muted);
  font-size: 15px;
  line-height: 1.5;
  font-weight: 750;
  text-align: center;
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--what-we-do-image-fallback) 82%, #ffffff), var(--what-we-do-image-fallback));
}
.what-we-do-badges {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}
.what-we-do-badge {
  position: absolute;
  display: grid;
  grid-template-columns: ${t.sizes.badgeIcon}px minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  width: min(300px, 42vw);
  min-height: 92px;
  padding: 18px 20px;
  border: 1px solid var(--what-we-do-border);
  border-radius: ${t.radius.badge}px;
  background: var(--what-we-do-badge-surface);
  box-shadow: var(--what-we-do-badge-shadow);
  backdrop-filter: blur(14px);
}
.what-we-do-badge:nth-child(1) {
  top: 8%;
  left: -92px;
}
.what-we-do-badge:nth-child(2) {
  left: 14%;
  bottom: -22px;
}
.what-we-do-badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${t.sizes.badgeIcon}px;
  height: ${t.sizes.badgeIcon}px;
  border-radius: ${t.radius.icon}px;
  color: var(--what-we-do-accent);
  background: var(--what-we-do-accent-soft);
}
.what-we-do-badge-icon svg {
  width: 31px;
  height: 31px;
}
.what-we-do-badge-body {
  min-width: 0;
}
.what-we-do-badge-title,
.what-we-do-badge-value,
.what-we-do-badge-text {
  margin: 0;
}
.what-we-do-badge-title {
  color: var(--what-we-do-text-soft);
  font-size: ${t.typography.badgeTitle.fontSize}px;
  line-height: ${t.typography.badgeTitle.lineHeight};
  font-weight: ${t.typography.badgeTitle.fontWeight};
  letter-spacing: ${t.typography.badgeTitle.letterSpacing};
}
.what-we-do-badge-value {
  margin-top: 6px;
  color: var(--what-we-do-text);
  font-size: ${t.typography.badgeValue.fontSize}px;
  line-height: ${t.typography.badgeValue.lineHeight};
  font-weight: ${t.typography.badgeValue.fontWeight};
  letter-spacing: ${t.typography.badgeValue.letterSpacing};
}
.what-we-do-badge-text {
  margin-top: 5px;
  color: var(--what-we-do-text-muted);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
}
@media (max-width: 1160px) {
  .what-we-do-shell {
    grid-template-columns: minmax(0, 0.86fr) minmax(380px, 1.14fr);
    gap: ${t.spacing.tabletGap}px;
  }
  .what-we-do-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .what-we-do-badge:nth-child(1) {
    left: -38px;
  }
  .what-we-do-badge {
    width: min(280px, 45vw);
  }
}
@media (max-width: 940px) {
  .what-we-do-shell {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }
  .what-we-do--reverse .what-we-do-content,
  .what-we-do-content {
    order: 1;
    max-width: 680px;
  }
  .what-we-do--reverse .what-we-do-media-column,
  .what-we-do-media-column {
    order: 2;
  }
  .what-we-do-media {
    min-height: 360px;
  }
  .what-we-do-image,
  .what-we-do-image-fallback {
    min-height: 360px;
  }
  .what-we-do-badge:nth-child(1) {
    left: 18px;
  }
  .what-we-do-badge:nth-child(2) {
    left: auto;
    right: 18px;
    bottom: -18px;
  }
}
@media (max-width: 700px) {
  .what-we-do {
    padding: ${t.spacing.mobileSectionY}px ${t.spacing.mobileSectionX}px;
  }
  .what-we-do-shell {
    gap: 28px;
  }
  .what-we-do-eyebrow {
    margin-bottom: 16px;
    font-size: 12px;
  }
  .what-we-do-title {
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: 1.16;
  }
  .what-we-do-description {
    margin-top: 14px;
    font-size: 14px;
  }
  .what-we-do-points {
    gap: 14px;
    margin-top: 24px;
  }
  .what-we-do-point {
    grid-template-columns: 26px minmax(0, 1fr);
    gap: 13px;
    font-size: ${t.typography.point.mobileFontSize}px;
  }
  .what-we-do-point-icon {
    width: 26px;
    height: 26px;
  }
  .what-we-do-point-icon svg {
    width: 15px;
    height: 15px;
  }
  .what-we-do-cta {
    width: 100%;
    justify-content: center;
    margin-top: 26px;
    padding: 0 18px;
  }
  .what-we-do-pattern {
    display: none;
  }
  .what-we-do-media,
  .what-we-do-image,
  .what-we-do-image-fallback {
    min-height: ${t.sizes.mobileMediaMinHeight}px;
  }
  .what-we-do-badges {
    position: static;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 12px;
    pointer-events: auto;
  }
  .what-we-do-badge,
  .what-we-do-badge:nth-child(1),
  .what-we-do-badge:nth-child(2) {
    position: static;
    width: 100%;
    min-height: 0;
    padding: 14px;
    gap: 12px;
  }
  .what-we-do-badge-icon {
    width: 44px;
    height: 44px;
  }
  .what-we-do-badge-icon svg {
    width: 25px;
    height: 25px;
  }
  .what-we-do-badge-title {
    font-size: 12px;
  }
  .what-we-do-badge-value {
    font-size: ${t.typography.badgeValue.mobileFontSize}px;
  }
}
@media (max-width: 460px) {
  .what-we-do-badges {
    grid-template-columns: minmax(0, 1fr);
  }
  .what-we-do-badge {
    grid-template-columns: 44px minmax(0, 1fr);
  }
}
`;

const iconTokenSet = new Set<string>(whatWeDoIconTokens);

function joinClassNames(...classNames: Array<string | false | null | undefined>) {
    return classNames.filter(Boolean).join(' ');
}

function getCssSize(value?: number | string) {
    if (typeof value === 'number') return `${value}px`;
    return value;
}

function getImage(image?: WhatWeDoImage | string): WhatWeDoImage | undefined {
    if (!image) return undefined;
    if (typeof image === 'string') return { src: image };
    return image;
}

function getImageSource(image?: WhatWeDoImage) {
    return image?.src || image?.url || image?.original || image?.thumbnail || '';
}

function getImageAlt(image: WhatWeDoImage | undefined, fallback: string) {
    if (image?.decorative) return '';
    return image?.alt || image?.title || fallback;
}

function kebabCase(value: string) {
    return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function toColorStyles(colors?: WhatWeDoColors): WhatWeDoCssVars {
    if (!colors) return {};

    return Object.entries(colors).reduce<WhatWeDoCssVars>((style, [token, value]) => {
        if (value) {
            style[`--what-we-do-${kebabCase(token)}`] = value;
        }

        return style;
    }, {});
}

function isIconToken(icon: unknown): icon is WhatWeDoIconToken {
    return typeof icon === 'string' && iconTokenSet.has(icon);
}

function isRenderableIconComponent(icon: WhatWeDoIcon): icon is React.ElementType {
    return (
        typeof icon === 'function' ||
        (typeof icon === 'object' &&
            icon !== null &&
            '$$typeof' in icon &&
            !React.isValidElement(icon))
    );
}

function hasIconContent(icon: WhatWeDoIcon | undefined): boolean {
    return icon !== undefined && icon !== null && icon !== false;
}

function renderIcon(icon: WhatWeDoIcon | undefined, className: string, size: number) {
    if (!hasIconContent(icon)) return null;

    const iconProps = {
        size,
        strokeWidth: 2.25,
        className,
        'aria-hidden': true,
        focusable: false,
    };

    if (isIconToken(icon)) {
        const Icon = iconRegistry[icon];
        return <Icon {...iconProps} />;
    }

    if (React.isValidElement(icon)) {
        const element = icon as React.ReactElement<any>;
        return React.cloneElement(element, {
            ...iconProps,
            ...(element.props || {}),
        });
    }

    if (isRenderableIconComponent(icon)) {
        const Icon = icon as React.ElementType;
        return <Icon {...iconProps} />;
    }

    return <span className={className}>{icon}</span>;
}

function getPointText(point: React.ReactNode | WhatWeDoPoint): React.ReactNode {
    if (
        typeof point === 'object' &&
        point !== null &&
        !React.isValidElement(point) &&
        ('text' in point || 'title' in point || 'label' in point)
    ) {
        return (
            (point as WhatWeDoPoint).text ??
            (point as WhatWeDoPoint).title ??
            (point as WhatWeDoPoint).label
        );
    }

    return point as React.ReactNode;
}

function normalizePoints(
    points: WhatWeDoProps['points'],
    pointIcon: WhatWeDoIcon,
): NormalizedPoint[] {
    const source = points !== undefined ? points : t.defaults.points;

    return source
        .map((point, index) => {
            const isConfig =
                typeof point === 'object' &&
                point !== null &&
                !React.isValidElement(point) &&
                ('text' in point || 'title' in point || 'label' in point || 'icon' in point);
            const pointConfig = isConfig ? (point as WhatWeDoPoint) : undefined;
            const text = getPointText(point);

            return {
                id: pointConfig?.id ?? index,
                text,
                icon: pointConfig?.icon ?? pointIcon,
                iconSrc: pointConfig?.iconSrc,
                iconAlt: pointConfig?.iconAlt,
                accentColor: pointConfig?.accentColour || pointConfig?.accentColor,
            };
        })
        .filter((point) => point.text !== undefined && point.text !== null && point.text !== '');
}

function normalizeBadges(badges?: WhatWeDoBadge[]): NormalizedBadge[] {
    const source = badges !== undefined ? badges : t.defaults.badges;

    return source
        .slice(0, 2)
        .map((badge, index) => {
            const title = badge.title ?? badge.label;

            return {
                id: badge.id ?? index,
                title,
                value: badge.value,
                text: badge.text,
                icon: badge.icon,
                iconSrc: badge.iconSrc,
                iconAlt: badge.iconAlt,
                accentColor: badge.accentColour || badge.accentColor,
                accentSoftColor: badge.accentSoftColour || badge.accentSoftColor,
            };
        })
        .filter((badge) => badge.title !== undefined && badge.title !== null);
}

function WhatWeDoLink({
    LinkComponent,
    href,
    className,
    title,
    ariaLabel,
    target,
    rel,
    children,
}: {
    LinkComponent?: WhatWeDoLinkComponent;
    href: string;
    className: string;
    title?: string;
    ariaLabel?: string;
    target?: string;
    rel?: string;
    children: React.ReactNode;
}) {
    if (LinkComponent) {
        return (
            <LinkComponent
                href={href}
                className={className}
                title={title}
                aria-label={ariaLabel}
                target={target}
                rel={rel}
            >
                {children}
            </LinkComponent>
        );
    }

    return (
        <a
            href={href}
            className={className}
            title={title}
            aria-label={ariaLabel}
            target={target}
            rel={rel}
        >
            {children}
        </a>
    );
}

function PointItem({ point }: { point: NormalizedPoint }) {
    const pointStyle = point.accentColor
        ? ({ '--what-we-do-accent': point.accentColor } as WhatWeDoCssVars)
        : undefined;

    return (
        <li className="what-we-do-point" style={pointStyle}>
            <span className="what-we-do-point-icon" aria-hidden="true">
                {point.iconSrc ? (
                    <img
                        src={point.iconSrc}
                        alt={point.iconAlt || ''}
                        className="what-we-do-icon-image"
                        loading="lazy"
                    />
                ) : (
                    renderIcon(point.icon, 'what-we-do-icon-node', 16)
                )}
            </span>
            <span>{point.text}</span>
        </li>
    );
}

function BadgeItem({ badge }: { badge: NormalizedBadge }) {
    const badgeStyle: WhatWeDoCssVars | undefined =
        badge.accentColor || badge.accentSoftColor
            ? {
                  '--what-we-do-accent': badge.accentColor,
                  '--what-we-do-accent-soft': badge.accentSoftColor,
              }
            : undefined;

    return (
        <div className="what-we-do-badge" style={badgeStyle}>
            <span className="what-we-do-badge-icon" aria-hidden="true">
                {badge.iconSrc ? (
                    <img
                        src={badge.iconSrc}
                        alt={badge.iconAlt || ''}
                        className="what-we-do-icon-image"
                        loading="lazy"
                    />
                ) : (
                    renderIcon(badge.icon, 'what-we-do-icon-node', 31)
                )}
            </span>
            <div className="what-we-do-badge-body">
                <p className="what-we-do-badge-title">{badge.title}</p>
                {badge.value ? <p className="what-we-do-badge-value">{badge.value}</p> : null}
                {badge.text ? <p className="what-we-do-badge-text">{badge.text}</p> : null}
            </div>
        </div>
    );
}

function WhatWeDo({
    eyebrow = t.defaults.eyebrow,
    title = t.defaults.title,
    description = t.defaults.description,
    points,
    pointIcon = t.defaults.pointIcon,
    badges,
    image,
    cta = t.defaults.cta,
    LinkComponent,
    Link,
    theme = 'light',
    darkMode,
    accentColor,
    accentColour,
    accentDarkColor,
    accentDarkColour,
    accentSoftColor,
    accentSoftColour,
    colors,
    colours,
    maxWidth,
    contentMaxWidth,
    reverse = false,
    showPattern = true,
    headingLevel = 'h2',
    id,
    className,
    style,
    ariaLabel,
}: WhatWeDoProps) {
    const resolvedTheme: WhatWeDoTheme = darkMode ? 'dark' : theme;
    const resolvedImage = getImage(image);
    const imageSrc = getImageSource(resolvedImage);
    const hasImage = !!imageSrc;
    const Heading = headingLevel;
    const pointItems = normalizePoints(points, pointIcon);
    const badgeItems = normalizeBadges(badges);
    const ResolvedLink = LinkComponent || Link;
    const ctaIcon =
        cta && (cta.iconSrc || hasIconContent(cta.icon) ? cta.icon : t.defaults.ctaIcon);

    const rootStyle: WhatWeDoCssVars = {
        ...style,
        ...toColorStyles(colours || colors),
        '--what-we-do-accent': accentColour || accentColor,
        '--what-we-do-accent-dark': accentDarkColour || accentDarkColor,
        '--what-we-do-accent-soft': accentSoftColour || accentSoftColor,
        '--what-we-do-max-width': getCssSize(maxWidth),
        '--what-we-do-content-max-width': getCssSize(contentMaxWidth),
    };

    return (
        <section
            id={id}
            className={joinClassNames('what-we-do', reverse && 'what-we-do--reverse', className)}
            data-theme={resolvedTheme}
            data-has-image={hasImage ? 'true' : 'false'}
            style={rootStyle}
            aria-label={ariaLabel}
        >
            <style>{whatWeDoCss}</style>

            <div className="what-we-do-shell">
                <div className="what-we-do-content">
                    {eyebrow ? <p className="what-we-do-eyebrow">{eyebrow}</p> : null}
                    {title ? <Heading className="what-we-do-title">{title}</Heading> : null}
                    {description ? <p className="what-we-do-description">{description}</p> : null}

                    {pointItems.length ? (
                        <ul className="what-we-do-points">
                            {pointItems.map((point) => (
                                <PointItem key={point.id} point={point} />
                            ))}
                        </ul>
                    ) : null}

                    {cta ? (
                        <WhatWeDoLink
                            LinkComponent={ResolvedLink}
                            href={cta.href}
                            className="what-we-do-cta"
                            title={cta.title}
                            ariaLabel={cta.ariaLabel}
                            target={cta.target}
                            rel={cta.rel}
                        >
                            <span>{cta.label}</span>
                            {cta.iconSrc ? (
                                <img
                                    src={cta.iconSrc}
                                    alt={cta.iconAlt || ''}
                                    className="what-we-do-icon-image"
                                    loading="lazy"
                                />
                            ) : ctaIcon ? (
                                renderIcon(ctaIcon, 'what-we-do-icon-node', 18)
                            ) : null}
                        </WhatWeDoLink>
                    ) : null}
                </div>

                <div className="what-we-do-media-column">
                    {showPattern ? (
                        <span className="what-we-do-pattern" aria-hidden="true" />
                    ) : null}

                    <div className="what-we-do-media">
                        {hasImage ? (
                            <img
                                src={imageSrc}
                                alt={getImageAlt(
                                    resolvedImage,
                                    typeof title === 'string' ? title : 'What we do',
                                )}
                                className="what-we-do-image"
                                width={resolvedImage?.width}
                                height={resolvedImage?.height}
                                loading={resolvedImage?.loading || 'lazy'}
                                decoding={resolvedImage?.decoding || 'async'}
                                style={
                                    resolvedImage?.objectPosition
                                        ? { objectPosition: resolvedImage.objectPosition }
                                        : undefined
                                }
                            />
                        ) : (
                            <div className="what-we-do-image-fallback">
                                Pass an image to feature your work.
                            </div>
                        )}
                    </div>

                    {badgeItems.length ? (
                        <div className="what-we-do-badges" aria-label="Service results">
                            {badgeItems.map((badge) => (
                                <BadgeItem key={badge.id} badge={badge} />
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

export { whatWeDoIconTokens, whatWeDoTokens } from './WhatWeDo.tokens';
export type { WhatWeDoColorToken, WhatWeDoIconToken } from './WhatWeDo.tokens';

export default WhatWeDo;
